import { Component, OnInit } from '@angular/core';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import {
    LyfisService,
    DiseaseOption,
    PartnerProfileOption,
    ScoreTrace,
} from '../../../services/lyfis.service';
import { LyfisAnswerType } from '../models/lyfis-questions-grid.models';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { IAnyoUser } from 'projects/app-core/src/auth/models/IAnyoUser';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { FileUtilsService } from 'projects/app-core/src/service/fileService';
import { MasterdataService } from '../../../services/masterdata.service';
import {
    LyfisMasterDataResponse,
    RuleEngineFacts,
} from '../../../models/masterDataResponse';

type NzSortOrder = 'ascend' | 'descend' | null;
type BulkResultsSortField =
    | 'scenarioId'
    | 'status'
    | 'drs'
    | 'pdh'
    | 'band'
    | 'rulesVersionId'
    | 'error'
    | 'labscore'
    | 'questionnaireScore'
    | 'wq'
    | 'wlab';

interface BulkResultsTableRow {
    rowIndex: number;
    scenarioId: string;
    success: boolean;
    status: 'OK' | 'FAILED';
    drs: number | null;
    pdh: number | null;
    band: string | null;
    rulesVersionId: string | null;
    error: string | null;
    labscore: number | null;
    questionnaireScore: number | null;
    wq: number | null;
    wlab: number | null;
    trace?: ScoreTrace | null;
}

@Component({
    selector: 'app-lyfis-testing',
    templateUrl: './lyfis-testing.component.html',
})
export class LyfisTestingComponent extends AnyoComponent implements OnInit {
    readonly LyfisAnswerType = LyfisAnswerType;

    override async ready(user?: IAnyoUser | undefined): Promise<void> {
        if (!user) return;

        try {
            const dropdown = await this.lyfisService.getDropdownValues().toPromise();
            this.diseases = dropdown?.enabledDiseases || [];

            await this.loadPartnerProfiles();
            await this.loadMasterData();
        } catch (err) {
            console.error(err);
        }
    }
    diseases: DiseaseOption[] = [];
    partnerProfiles: PartnerProfileOption[] = [];

    // Manual tester
    manualPartnerProfileId = '';
    manualDiseaseId = '';
    factRows: Array<{
        key: string;
        value: string | number | boolean;
        answerType?: LyfisAnswerType;
        availableOptions?: Array<{ key: string; label: string }>;
        validation?: { min?: number; max?: number; regex?: string };
        placeholder?: string;
        description?: string;
    }> = [];
    labRows: Array<{
        markerKey: string;
        value: number | null;
        reportDate: string;
        description?: string;
    }> = [
            {
                markerKey: '',
                value: null,
                reportDate: new Date().toISOString(),
            },
        ];
    manualResult?: unknown;
    manualResultTrace?: ScoreTrace | null;
    manualLoading = false;
    bulkLoading = false;
    bulkScenarioOrder: string[] = [];
    bulkScenarios: Array<{
        scenarioId?: string;
        questionnaireFacts: Record<string, unknown>;
        labMarkersByDisease: Record<
            string,
            Record<string, { value: number; reportDate?: string; daysSinceReport?: number }>
        >;
    }> = [];
    bulkResults: Array<{
        scenarioId?: string;
        success: boolean;
        output?: {
            diseases: Array<{
                diseaseId: string;
                drsScore: number;
                pdhScore: number;
                questionnaireScore: number;
                labMarkersScore: number;
                band: string;
                wQ: number;
                wL: number;
                rulesVersionId: string;
                trace?: ScoreTrace | null;
            }>;
        };
        error?: string;
        labscore?: number | null;
        questionnaireScore?: number | null;
        wq?: number | null;
        wlab?: number | null;
    }> = [];

    // Bulk Results table (local filter + sort)
    bulkResultsTableRaw: BulkResultsTableRow[] = [];
    bulkResultsTableView: BulkResultsTableRow[] = [];

    bulkResultsPageSizeOptions = [10, 20, 50, 100];
    bulkResultsPageSize = 20;

    bulkScenarioSearchText = '';
    bulkRulesVersionIdSearchText = '';
    bulkErrorSearchText = '';

    bulkStatusFilterOptions: Array<{ label: 'OK' | 'FAILED'; value: 'OK' | 'FAILED'; checked: boolean }> = [
        { label: 'OK', value: 'OK', checked: true },
        { label: 'FAILED', value: 'FAILED', checked: true },
    ];

    bulkBandFilterOptions: Array<{ label: string; value: string; checked: boolean }> = [];

    bulkResultsSortField: BulkResultsSortField | null = null;
    bulkResultsSortOrder: NzSortOrder = null;

    // Expandable rows state
    expandedRows = new Set<string>();

    get manualPreviewFields(): {
        questionnaireScore?: unknown;
        labMarkersScore?: unknown;
        drsScore?: unknown;
        pdhScore?: unknown;
        wQ?: unknown;
        wL?: unknown;
        rulesVersionId?: unknown;
        band?: unknown;
    } | null {
        const result = this.manualResult;
        if (!result || typeof result !== 'object') return null;

        const diseaseOutput = (result as { diseases?: unknown[] })?.diseases?.[0];
        if (!diseaseOutput || typeof diseaseOutput !== 'object') return null;

        const d = diseaseOutput as Record<string, unknown>;
        return {
            questionnaireScore: d['questionnaireScore'],
            labMarkersScore: d['labMarkersScore'],
            drsScore: d['drsScore'],
            pdhScore: d['pdhScore'],
            wQ: d['wQ'],
            wL: d['wL'],
            rulesVersionId: d['rulesVersionId'],
            band: d['band'],
        };
    }

    toDisplayString(value: unknown): string {
        if (value === null || value === undefined || value === '') return '—';
        if (typeof value === 'string') return value;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        try {
            return JSON.stringify(value);
        } catch (_err: unknown) {
            return String(value);
        }
    }

    getBandColor(band: unknown): string {
        const b = this.toDisplayString(band).toLowerCase();
        if (b === 'low') return 'red';
        if (b === 'moderate' || b === 'medium') return 'orange';
        if (b === 'high') return 'blue';
        if (b === 'excellent') return 'green';
        return 'default';
    }

    shortenMiddle(value: unknown, head = 10, tail = 8): string {
        const text = this.toDisplayString(value);
        if (text === '—') return text;
        if (text.length <= head + tail + 3) return text;
        return `${text.slice(0, head)}...${text.slice(-tail)}`;
    }

    async copyToClipboard(value: unknown) {
        const text = this.toDisplayString(value);
        if (!text || text === '—') return;
        try {
            await navigator.clipboard.writeText(text);
            this.toast.showSuccess('Copied');
        } catch (err) {
            console.error(err);
            this.toast.showAnyoErrorToast('Copy failed');
        }
    }

    // Master data for decoding fact values
    private lyfisMasterData: LyfisMasterDataResponse | null = null;
    private factOptionsMap: Map<string, Map<string, string>> = new Map();

    constructor(
        private lyfisService: LyfisService,
        private toast: ToastService,
        private fileUtils: FileUtilsService,
        private masterdataService: MasterdataService,
        auth: AuthService,
    ) {
        super(auth);
    }


    async loadPartnerProfiles() {
        try {
            const partners = await this.lyfisService
                .getPartnerProfilesForDropdown()
                .toPromise();
            const partnerResp = partners as {
                data?: Array<{
                    _id?: string;
                    id?: string;
                    partnerId?: string;
                    enabledDiseases?: string[];
                }>;
            };
            this.partnerProfiles =
                partnerResp?.data?.map((p) => ({
                    id: p._id || p.id || '',
                    name: p.partnerId || 'Default profile',
                    enabledDiseases: p.enabledDiseases || [],
                })) || [];
            if (!this.partnerProfiles.length) {
                this.toast.showAnyoErrorToast('No partner profiles found');
            }
            // If none selected, pick the first active/default
            if (!this.manualPartnerProfileId && this.partnerProfiles.length) {
                this.manualPartnerProfileId = this.partnerProfiles[0].id;
                this.ensureDiseaseSelectionValid();
                await this.refreshRequirements();
            }
        } catch (err) {
            console.error(err);
            this.toast.showAnyoErrorToast('Failed to load partner profiles');
        }
    }

    get filteredDiseases(): DiseaseOption[] {
        const profile = this.partnerProfiles.find((p) => p.id === this.manualPartnerProfileId);
        const enabled = profile?.enabledDiseases || [];
        if (!enabled.length) return [];
        return (this.diseases || []).filter((d) => enabled.includes(d.id));
    }

    async onPartnerProfileChange() {
        this.ensureDiseaseSelectionValid();
        await this.refreshRequirements();
        this.rebuildBulkResultsTable();
    }

    async onDiseaseChange() {
        await this.refreshRequirements();
        this.rebuildBulkResultsTable();
    }

    private ensureDiseaseSelectionValid() {
        const allowed = new Set(this.filteredDiseases.map((d) => d.id));
        if (this.manualDiseaseId && allowed.has(this.manualDiseaseId)) {
            return;
        }
        this.manualDiseaseId = this.filteredDiseases[0]?.id || '';
    }

    private async refreshRequirements() {
        this.factRows = [];
        this.labRows = [];

        if (!this.manualPartnerProfileId || !this.manualDiseaseId) {
            return;
        }

        try {
            const res = await this.lyfisService
                .getDiseaseRequirements(this.manualPartnerProfileId, this.manualDiseaseId)
                .toPromise();

            const requiredFacts = res?.requiredQuestionnaireFacts || [];
            const requiredMarkers = res?.requiredLabMarkers || [];

            const meta = res?.factMetadata || [];
            const metaByKey = new Map(
                (meta || [])
                    .filter((m) => m?.factKey)
                    .map((m) => [m.factKey, m]),
            );

            this.factRows = requiredFacts.map((k) => {
                const m = metaByKey.get(k);
                return {
                    key: k,
                    value: '',
                    answerType: m?.answerType,
                    availableOptions: m?.availableOptions || undefined,
                    validation: m?.validation || undefined,
                    placeholder: m?.placeholder || undefined,
                    description: m?.description || undefined,
                };
            });

            const markerMeta = res?.labMarkerMetadata || [];
            const markerMetaByKey = new Map(
                (markerMeta || [])
                    .filter((m) => m?.markerKey)
                    .map((m) => [m.markerKey, m]),
            );

            this.labRows = requiredMarkers.map((k) => {
                const m = markerMetaByKey.get(k);
                return {
                    markerKey: k,
                    value: null,
                    reportDate: new Date().toISOString(),
                    description: m?.description || undefined,
                };
            });
        } catch (err) {
            console.error(err);
            this.toast.showAnyoErrorToast('Failed to load required facts/markers for disease');
        }
    }

    async runManualPreview() {
        this.manualLoading = true;
        this.manualResult = undefined;
        this.manualResultTrace = undefined;
        try {
            const questionnaireFacts = this.buildFacts();
            const labMarkersByDisease = this.buildLabs();
            const diseases = this.manualDiseaseId ? [this.manualDiseaseId] : [];

            const result = await this.lyfisService
                .previewLyfisScores({
                    partnerProfileId: this.manualPartnerProfileId,
                    questionnaireFacts,
                    labMarkersByDisease,
                    diseases,
                })
                .toPromise();
            this.manualResult = result;
            // Extract trace from first disease result
            const firstDisease = result?.diseases?.[0];
            this.manualResultTrace = firstDisease?.trace || null;
        } catch (err) {
            console.error(err);
            this.toast.showAnyoErrorToast('Preview failed. Check inputs.');
        } finally {
            this.manualLoading = false;
        }
    }

    exportCsvTemplate() {
        if (!this.manualPartnerProfileId || !this.manualDiseaseId) {
            this.toast.showAnyoErrorToast('Select partner profile + disease first');
            return;
        }
        if (!this.factRows?.length && !this.labRows?.length) {
            this.toast.showAnyoErrorToast(
                'No required facts/markers loaded for export. Select partner profile + disease.',
            );
            return;
        }

        const row: Record<string, string> = {};
        row['scenarioId'] = '';

        (this.factRows || []).forEach((r) => {
            if (!r?.key) return;
            row[`fact:${r.key}`] = '';
        });

        (this.labRows || []).forEach((r) => {
            if (!r?.markerKey) return;
            row[`lab:${r.markerKey}`] = '';
            row[`labDate:${r.markerKey}`] = '';
        });

        this.fileUtils.exportCSV([row], `lyfis-${this.manualDiseaseId}-template`);

        // Export reference metadata CSV
        const referenceRows: Array<Record<string, string | number | undefined>> =
            [];

        // Add fact rows with expanded options
        (this.factRows || []).forEach((factRow) => {
            if (!factRow?.key) return;

            const baseRow: Record<string, string | number | undefined> = {
                rowType: 'fact',
                diseaseId: this.manualDiseaseId,
                partnerProfileId: this.manualPartnerProfileId,
                key: factRow.key,
                answerType: factRow.answerType || '',
                description: factRow.description || '',
                validationMin: factRow.validation?.min,
                validationMax: factRow.validation?.max,
                validationRegex: factRow.validation?.regex || '',
                placeholder: factRow.placeholder || '',
            };

            if (
                factRow.availableOptions &&
                Array.isArray(factRow.availableOptions) &&
                factRow.availableOptions.length > 0
            ) {
                // Emit one row per option
                factRow.availableOptions.forEach((opt) => {
                    referenceRows.push({
                        ...baseRow,
                        optionKey: opt.key,
                        optionLabel: opt.label,
                    });
                });
            } else {
                // Emit one row with blank optionKey/optionLabel
                referenceRows.push({
                    ...baseRow,
                    optionKey: '',
                    optionLabel: '',
                });
            }
        });

        // Add lab marker rows
        (this.labRows || []).forEach((labRow) => {
            if (!labRow?.markerKey) return;

            referenceRows.push({
                rowType: 'lab_marker',
                diseaseId: this.manualDiseaseId,
                partnerProfileId: this.manualPartnerProfileId,
                key: labRow.markerKey,
                answerType: '',
                description: labRow.description || '',
                optionKey: '',
                optionLabel: '',
                validationMin: undefined,
                validationMax: undefined,
                validationRegex: '',
                placeholder: '',
            });
        });

        if (referenceRows.length > 0) {
            this.fileUtils.exportCSV(
                referenceRows,
                `lyfis-${this.manualDiseaseId}-reference-meta`,
            );
        }
    }

    async onBulkCsvSelected(event: Event) {
        const input = event.target as HTMLInputElement | null;
        const file = input?.files?.[0];
        if (!file) return;

        if (!this.manualPartnerProfileId || !this.manualDiseaseId) {
            this.toast.showAnyoErrorToast('Select partner profile + disease first');
            if (input) input.value = '';
            return;
        }

        try {
            const rows = (await this.fileUtils.parseCSV<Record<string, string>>(file)) || [];
            const factKeys = (this.factRows || []).map((r) => r.key).filter(Boolean);
            const markerKeys = (this.labRows || []).map((r) => r.markerKey).filter(Boolean);

            const seen = new Map<string, number>();
            const scenarioOrder: string[] = [];
            const scenarios: Array<{
                scenarioId?: string;
                questionnaireFacts: Record<string, unknown>;
                labMarkersByDisease: Record<
                    string,
                    Record<string, { value: number; reportDate?: string }>
                >;
            }> = [];
            const preResults: Array<{
                scenarioId?: string;
                success: boolean;
                error?: string;
            }> = [];

            rows.forEach((row, idx) => {
                const baseId = (row as Record<string, unknown>)?.['scenarioId']
                    ? String((row as Record<string, unknown>)['scenarioId'])
                    : `row-${idx + 1}`;
                const count = seen.get(baseId) || 0;
                seen.set(baseId, count + 1);
                const scenarioId = count ? `${baseId}__${count + 1}` : baseId;

                const questionnaireFacts: Record<string, unknown> = {};
                let hasAny = false;
                factKeys.forEach((k) => {
                    const v = (row as Record<string, unknown>)[`fact:${k}`];
                    if (v === undefined || v === null || v === '') return;
                    hasAny = true;
                    questionnaireFacts[k] = this.safeParse(String(v));
                });

                const labMarkers: Record<string, { value: number; reportDate?: string }> = {};
                for (const mk of markerKeys) {
                    const raw = (row as Record<string, unknown>)[`lab:${mk}`];
                    if (raw === undefined || raw === null || raw === '') continue;
                    hasAny = true;
                    const num = Number(raw);
                    if (Number.isNaN(num)) {
                        scenarioOrder.push(scenarioId);
                        preResults.push({
                            scenarioId,
                            success: false,
                            error: `Invalid number for lab:${mk}`,
                        });
                        return;
                    }
                    const dateRaw = (row as Record<string, unknown>)[`labDate:${mk}`];
                    labMarkers[mk] = {
                        value: num,
                        reportDate: dateRaw ? String(dateRaw) : new Date().toISOString(),
                    };
                }

                if (!hasAny) {
                    return;
                }

                scenarioOrder.push(scenarioId);
                scenarios.push({
                    scenarioId,
                    questionnaireFacts,
                    labMarkersByDisease: {
                        [this.manualDiseaseId]: labMarkers,
                    },
                });
            });

            this.bulkScenarioOrder = scenarioOrder;
            this.bulkScenarios = scenarios as unknown as LyfisTestingComponent['bulkScenarios'];
            this.bulkResults = preResults as unknown as LyfisTestingComponent['bulkResults'];
            this.rebuildBulkResultsTable();

            if (!scenarioOrder.length) {
                this.toast.showAnyoErrorToast('No scenarios found in CSV');
            } else {
                this.toast.showSuccess(`Loaded ${scenarioOrder.length} scenario(s)`);
            }
        } catch (err) {
            console.error(err);
            this.toast.showAnyoErrorToast('Failed to parse CSV');
        } finally {
            if (input) input.value = '';
        }
    }

    getSelectedDiseaseFromOutput(
        output: LyfisTestingComponent['bulkResults'][number]['output'],
    ) {
        const diseaseId = this.manualDiseaseId;
        const diseases = output?.diseases || [];
        return diseases.find((d) => d.diseaseId === diseaseId) || diseases[0];
    }

    async runBulkPreview() {
        if (!this.manualPartnerProfileId || !this.manualDiseaseId) {
            this.toast.showAnyoErrorToast('Select partner profile + disease first');
            return;
        }
        if (!this.bulkScenarioOrder.length) {
            this.toast.showAnyoErrorToast('Import CSV first');
            return;
        }

        const scenariosToRun = this.bulkScenarios || [];
        if (!scenariosToRun.length) {
            // All rows were parse-errors; nothing to call backend for.
            return;
        }

        this.bulkLoading = true;
        try {
            const resp = await this.lyfisService
                .previewLyfisScoresBulk({
                    partnerProfileId: this.manualPartnerProfileId,
                    diseases: [this.manualDiseaseId],
                    scenarios: scenariosToRun,
                })
                .toPromise();

            const apiResults = resp?.results || [];
            const resultById = new Map<
                string,
                (LyfisTestingComponent['bulkResults'][number] & { scenarioId?: string })
            >();

            // seed existing parse-errors
            (this.bulkResults || []).forEach((r) => {
                if (r?.scenarioId) resultById.set(r.scenarioId, r);
            });

            apiResults.forEach((r) => {
                if (!r?.scenarioId) return;
                resultById.set(r.scenarioId, r as unknown as LyfisTestingComponent['bulkResults'][number]);
            });

            this.bulkResults = this.bulkScenarioOrder
                .map((id) => resultById.get(id))
                .filter(Boolean) as LyfisTestingComponent['bulkResults'];
            this.rebuildBulkResultsTable();
        } catch (err) {
            console.error(err);
            this.toast.showAnyoErrorToast('Bulk preview failed');
        } finally {
            this.bulkLoading = false;
        }
    }

    exportBulkResultsCsv() {
        if (!this.bulkResultsTableView?.length && !this.bulkResults?.length) {
            this.toast.showAnyoErrorToast('No bulk results to export');
            return;
        }
        // Export the currently visible table (after filters/sort)
        const source = this.bulkResultsTableView?.length
            ? this.bulkResultsTableView
            : this.buildBulkResultsTableRows(this.bulkResults || []);

        const rows = source.map((r) => ({
            scenarioId: r.scenarioId || '',
            status: r.status,
            drs: r.drs ?? '',
            pdh: r.pdh ?? '',
            band: r.band ?? '',
            rulesVersionId: r.rulesVersionId ?? '',
            error: r.error ?? '',
            labscore: r.labscore ?? '',
            questionnaireScore: r.questionnaireScore ?? '',
            wq: r.wq ?? '',
            wlab: r.wlab ?? '',
        }));

        this.fileUtils.exportCSV(rows, `lyfis-${this.manualDiseaseId}-bulk-results-table`);
    }

    // Bulk Results table helpers
    get bulkScenarioSearchActive(): boolean {
        return !!this.bulkScenarioSearchText?.trim();
    }
    get bulkRulesSearchActive(): boolean {
        return !!this.bulkRulesVersionIdSearchText?.trim();
    }
    get bulkErrorSearchActive(): boolean {
        return !!this.bulkErrorSearchText?.trim();
    }
    get bulkStatusFilterActive(): boolean {
        return (this.bulkStatusFilterOptions || []).some((o) => !o.checked);
    }
    get bulkBandFilterActive(): boolean {
        const opts = this.bulkBandFilterOptions || [];
        if (!opts.length) return false;
        return opts.some((o) => !o.checked);
    }

    private buildBulkResultsTableRows(
        results: LyfisTestingComponent['bulkResults'],
    ): BulkResultsTableRow[] {
        return (results || []).map((r, idx) => {
            const selected = r.success ? this.getSelectedDiseaseFromOutput(r.output) : undefined;
            return {
                rowIndex: idx,
                scenarioId: r.scenarioId || '—',
                success: !!r.success,
                status: r.success ? 'OK' : 'FAILED',
                drs: r.success ? (selected?.drsScore ?? null) : null,
                pdh: r.success ? (selected?.pdhScore ?? null) : null,
                band: r.success ? (selected?.band ?? null) : null,
                rulesVersionId: r.success ? (selected?.rulesVersionId ?? null) : null,
                error: r.error ?? null,
                labscore: r.success ? (selected?.labMarkersScore ?? null) : null,
                questionnaireScore: r.success ? (selected?.questionnaireScore ?? null) : null,
                wq: r.success ? (selected?.wQ ?? null) : null,
                wlab: r.success ? (selected?.wL ?? null) : null,
                trace: r.success ? (selected?.trace ?? null) : null,
            };
        });
    }

    private rebuildBulkResultsTable() {
        const prevBandChecked = new Map(
            (this.bulkBandFilterOptions || []).map((o) => [o.value, o.checked] as const),
        );

        this.bulkResultsTableRaw = this.buildBulkResultsTableRows(this.bulkResults || []);

        const bands = Array.from(
            new Set(
                (this.bulkResultsTableRaw || [])
                    .map((r) => r.band)
                    .filter((b): b is string => !!b && b !== '—'),
            ),
        ).sort((a, b) => a.localeCompare(b));

        this.bulkBandFilterOptions = bands.map((b) => ({
            label: b,
            value: b,
            checked: prevBandChecked.get(b) ?? true,
        }));

        this.applyBulkResultsTableFiltersAndSort();
    }

    applyBulkResultsTableFiltersAndSort() {
        const scenarioQ = (this.bulkScenarioSearchText || '').trim().toLowerCase();
        const rulesQ = (this.bulkRulesVersionIdSearchText || '').trim().toLowerCase();
        const errorQ = (this.bulkErrorSearchText || '').trim().toLowerCase();

        const allowedStatus = new Set(
            (this.bulkStatusFilterOptions || []).filter((o) => o.checked).map((o) => o.value),
        );
        const allowedBands = new Set(
            (this.bulkBandFilterOptions || []).filter((o) => o.checked).map((o) => o.value),
        );

        const bandFilterActive = (this.bulkBandFilterOptions || []).length > 0
            && allowedBands.size !== (this.bulkBandFilterOptions || []).length;

        let rows = [...(this.bulkResultsTableRaw || [])];

        // Column search
        if (scenarioQ) {
            rows = rows.filter((r) => (r.scenarioId || '').toLowerCase().includes(scenarioQ));
        }
        if (rulesQ) {
            rows = rows.filter((r) => (r.rulesVersionId || '').toLowerCase().includes(rulesQ));
        }
        if (errorQ) {
            rows = rows.filter((r) => (r.error || '').toLowerCase().includes(errorQ));
        }

        // Filters
        rows = rows.filter((r) => allowedStatus.has(r.status));
        if (bandFilterActive) {
            // Only apply band filter where band exists (successful rows)
            rows = rows.filter((r) => !r.success || (r.band ? allowedBands.has(r.band) : true));
        }

        // Sort
        const field = this.bulkResultsSortField;
        const order = this.bulkResultsSortOrder;

        const compareStrings = (a: string | null, b: string | null) => {
            const av = (a || '').toString();
            const bv = (b || '').toString();
            return av.localeCompare(bv);
        };
        const compareNumbers = (a: number | null, b: number | null) => {
            if (a === null && b === null) return 0;
            if (a === null) return 1;
            if (b === null) return -1;
            return a - b;
        };

        if (field && order) {
            rows.sort((a, b) => {
                let cmp = 0;
                switch (field) {
                    case 'scenarioId':
                        cmp = compareStrings(a.scenarioId, b.scenarioId);
                        break;
                    case 'status':
                        cmp = compareStrings(a.status, b.status);
                        break;
                    case 'drs':
                        cmp = compareNumbers(a.drs, b.drs);
                        break;
                    case 'pdh':
                        cmp = compareNumbers(a.pdh, b.pdh);
                        break;
                    case 'band':
                        cmp = compareStrings(a.band, b.band);
                        break;
                    case 'labscore':
                        cmp = compareNumbers(a.labscore, b.labscore);
                        break;
                    case 'questionnaireScore':
                        cmp = compareNumbers(a.questionnaireScore, b.questionnaireScore);
                        break;
                    case 'wq':
                        cmp = compareNumbers(a.wq, b.wq);
                        break;
                    case 'wlab':
                        cmp = compareNumbers(a.wlab, b.wlab);
                        break;
                    case 'rulesVersionId':
                        cmp = compareStrings(a.rulesVersionId, b.rulesVersionId);
                        break;
                    case 'error':
                        cmp = compareStrings(a.error, b.error);
                        break;
                    default:
                        cmp = 0;
                }
                return order === 'ascend' ? cmp : -cmp;
            });
        } else {
            // Default ordering: keep original order from API/csv
            rows.sort((a, b) => a.rowIndex - b.rowIndex);
        }

        this.bulkResultsTableView = rows;
    }

    sortBulkResultsTable(field: BulkResultsSortField, order: string | null) {
        const sortOrder: NzSortOrder = order === 'ascend' || order === 'descend' ? order : null;
        this.bulkResultsSortField = sortOrder ? field : null;
        this.bulkResultsSortOrder = sortOrder;
        this.applyBulkResultsTableFiltersAndSort();
    }

    bulkStatusFilter(values: string[]) {
        const set = new Set(values || []);
        this.bulkStatusFilterOptions = (this.bulkStatusFilterOptions || []).map((o) => ({
            ...o,
            checked: set.has(o.value),
        }));
    }

    bulkBandFilter(values: string[]) {
        const set = new Set(values || []);
        this.bulkBandFilterOptions = (this.bulkBandFilterOptions || []).map((o) => ({
            ...o,
            checked: set.has(o.value),
        }));
    }

    bulkResultsFilterOk() {
        this.applyBulkResultsTableFiltersAndSort();
    }

    resetBulkResultsTableFilters() {
        this.bulkScenarioSearchText = '';
        this.bulkRulesVersionIdSearchText = '';
        this.bulkErrorSearchText = '';
        this.bulkResultsSortField = null;
        this.bulkResultsSortOrder = null;

        this.bulkStatusFilterOptions = (this.bulkStatusFilterOptions || []).map((o) => ({
            ...o,
            checked: true,
        }));
        this.bulkBandFilterOptions = (this.bulkBandFilterOptions || []).map((o) => ({
            ...o,
            checked: true,
        }));

        this.applyBulkResultsTableFiltersAndSort();
    }

    addFactRow() {
        this.factRows.push({ key: '', value: '' });
    }

    removeFactRow(idx: number) {
        this.factRows.splice(idx, 1);
    }

    addLabRow() {
        this.labRows.push({
            markerKey: '',
            value: null,
            reportDate: new Date().toISOString(),
        });
    }

    removeLabRow(idx: number) {
        this.labRows.splice(idx, 1);
    }

    private buildFacts(): Record<string, unknown> {
        const facts: Record<string, unknown> = {};
        this.factRows.forEach((row) => {
            if (!row.key) return;
            if (row.value === undefined || row.value === null || row.value === '') {
                return;
            }
            if (typeof row.value === 'string') {
                facts[row.key] = this.safeParse(row.value);
                return;
            }
            facts[row.key] = row.value;
        });
        return facts;
    }

    private buildLabs(): Record<
        string,
        Record<string, { value: number; reportDate: string }>
    > {
        const labs: Record<
            string,
            Record<string, { value: number; reportDate: string }>
        > = {};
        const diseaseId = this.manualDiseaseId;
        if (!diseaseId) return labs;
        this.labRows.forEach((row) => {
            if (!row.markerKey || row.value === null) {
                return;
            }
            if (!labs[diseaseId]) {
                labs[diseaseId] = {};
            }
            labs[diseaseId][row.markerKey] = {
                value: Number(row.value),
                reportDate: row.reportDate || new Date().toISOString(),
            };
        });
        return labs;
    }

    private safeParse(value: string): unknown {
        try {
            return value ? JSON.parse(value) : value;
        } catch (_err: unknown) {
            return value;
        }
    }

    /**
     * Toggle expandable row for a scenario
     */
    toggleRowExpansion(scenarioId: string): void {
        if (this.expandedRows.has(scenarioId)) {
            this.expandedRows.delete(scenarioId);
        } else {
            this.expandedRows.add(scenarioId);
        }
    }

    /**
     * Check if a row is expanded
     */
    isRowExpanded(scenarioId: string): boolean {
        return this.expandedRows.has(scenarioId);
    }

    /**
     * Get input data for a scenario
     */
    getScenarioInputs(scenarioId: string): {
        questionnaireFacts: Record<string, unknown>;
        labMarkers: Record<string, { value: number; reportDate?: string }>;
    } | null {
        const scenario = this.bulkScenarios.find((s) => s.scenarioId === scenarioId);
        if (!scenario) return null;

        const diseaseId = this.manualDiseaseId;
        const labMarkers = diseaseId
            ? scenario.labMarkersByDisease?.[diseaseId] || {}
            : {};

        return {
            questionnaireFacts: scenario.questionnaireFacts || {},
            labMarkers: labMarkers,
        };
    }

    /**
     * Check if an object has any keys
     */
    hasKeys(obj: Record<string, unknown> | null | undefined): boolean {
        return obj ? Object.keys(obj).length > 0 : false;
    }

    /**
     * Load master data for decoding fact values
     */
    private async loadMasterData(): Promise<void> {
        try {
            const response = await this.masterdataService
                .lyfisMasterDataList()
                .toPromise();
            this.lyfisMasterData = response || null;

            // Build a map: factKey -> optionKey -> label
            this.factOptionsMap.clear();
            if (this.lyfisMasterData?.RuleEngineFacts) {
                this.lyfisMasterData.RuleEngineFacts.forEach((fact) => {
                    // factKey might not be in the interface but exists in the API response
                    const factKey = (fact as unknown as { factKey?: string })
                        .factKey;
                    if (factKey && fact.availableOptions) {
                        const optionMap = new Map<string, string>();
                        fact.availableOptions.forEach((option) => {
                            if (option.key && option.label) {
                                optionMap.set(option.key, option.label);
                            }
                        });
                        if (optionMap.size > 0) {
                            this.factOptionsMap.set(factKey, optionMap);
                        }
                    }
                });
            }
        } catch (err) {
            console.error('Failed to load master data', err);
        }
    }

    /**
     * Decode a fact value from ID to label
     * Returns the label if found, otherwise returns the original value
     */
    decodeFactValue(factKey: string, value: unknown): string {
        if (!factKey || value === null || value === undefined) {
            return this.toDisplayString(value);
        }

        const valueStr = String(value);
        const optionMap = this.factOptionsMap.get(factKey);

        if (optionMap) {
            const label = optionMap.get(valueStr);
            if (label) {
                return label;
            }
        }

        // If not found in options, return original value
        return this.toDisplayString(value);
    }

    /**
     * Open trace details in a new tab with full-page view
     * Uses localStorage to avoid HTTP 431 (header too large) errors
     */
    openTraceInNewTab(row: BulkResultsTableRow): void {
        if (!row.trace) {
            this.toast.showAnyoErrorToast('No trace data available for this scenario');
            return;
        }

        const inputs = this.getScenarioInputs(row.scenarioId);
        const disease = this.filteredDiseases.find((d) => d.id === this.manualDiseaseId);

        const traceData = {
            scenarioId: row.scenarioId,
            diseaseId: this.manualDiseaseId,
            diseaseName: disease?.data || this.manualDiseaseId,
            drsScore: row.drs,
            pdhScore: row.pdh,
            band: row.band,
            trace: row.trace,
            questionnaireFacts: inputs?.questionnaireFacts,
            labMarkers: inputs?.labMarkers,
        };

        // Use localStorage to pass large data (avoids HTTP 431 error)
        const traceKey = `lyfis-trace-${Date.now()}`;
        localStorage.setItem(traceKey, JSON.stringify(traceData));
        window.open(`/lyfis/trace-detail?key=${traceKey}`, '_blank');
    }

    /**
     * Open manual test trace in a new tab
     * Uses localStorage to avoid HTTP 431 (header too large) errors
     */
    openManualTraceInNewTab(): void {
        if (!this.manualResultTrace) {
            this.toast.showAnyoErrorToast('No trace data available');
            return;
        }

        const disease = this.filteredDiseases.find((d) => d.id === this.manualDiseaseId);
        const result = this.manualResult as {
            diseases?: Array<{ drsScore?: number; pdhScore?: number; band?: string }>;
        };
        const firstDisease = result?.diseases?.[0];

        const traceData = {
            scenarioId: 'Manual Test',
            diseaseId: this.manualDiseaseId,
            diseaseName: disease?.data || this.manualDiseaseId,
            drsScore: firstDisease?.drsScore,
            pdhScore: firstDisease?.pdhScore,
            band: firstDisease?.band,
            trace: this.manualResultTrace,
            questionnaireFacts: this.buildFacts(),
            labMarkers: this.buildLabs()[this.manualDiseaseId] || {},
        };

        // Use localStorage to pass large data (avoids HTTP 431 error)
        const traceKey = `lyfis-trace-${Date.now()}`;
        localStorage.setItem(traceKey, JSON.stringify(traceData));
        window.open(`/lyfis/trace-detail?key=${traceKey}`, '_blank');
    }
}

